import { useRoute, useLocation } from "wouter-preact"
import { useState } from "preact/compat"
import { useAsyncEffect } from "@/utils"
import { SettingsLayout } from "../layout"
import { IntegrationWithJobInfo, getIntegrationsWithJobs, findIntegrationByName, findIntegrationById } from "@/api/integration"
import { Button } from "@/components/ui/button"
import { LoadingSpinner } from "@/app/loading"
import { IntegrationSettingsContent } from "./integration-settings-content"

function useIntegrationSettingsLoader(
  lookupKey: string,
  lookup: (integrations: IntegrationWithJobInfo[]) => IntegrationWithJobInfo | undefined
) {
  const [integration, setIntegration] = useState<IntegrationWithJobInfo | null>(null)
  const [loading, setLoading] = useState(true)

  async function refreshIntegration() {
    const res = await getIntegrationsWithJobs()
    if (res.ok) {
      const found = lookup(res.value.data)
      if (found) {
        setIntegration(found)
      }
    }
  }

  useAsyncEffect(async () => {
    setLoading(true)
    await refreshIntegration()
    setLoading(false)
  }, [lookupKey])

  return { integration, loading, refreshIntegration }
}

export function IntegrationSettingsPage() {
  const [, params] = useRoute("/settings/config/:name")
  const [, setLocation] = useLocation()
  const integrationName = decodeURIComponent(params!.name)

  const { integration, loading, refreshIntegration } = useIntegrationSettingsLoader(
    integrationName,
    integrations => findIntegrationByName(integrations, integrationName)
  )

  if (loading) {
    return (
      <SettingsLayout>
        <div class="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      </SettingsLayout>
    )
  }

  if (!integration) {
    return (
      <SettingsLayout>
        <div class="text-center py-12">
          <p class="text-gray-500 mb-4">Integration not found</p>
          <Button variant="outline" onClick={() => setLocation("/settings/connections")}>
            Back to Connections
          </Button>
        </div>
      </SettingsLayout>
    )
  }

  return <IntegrationSettingsContent integration={integration} onRefresh={refreshIntegration} />
}

export function IntegrationSettingsByIdPage() {
  const [, params] = useRoute("/settings/config/id/:id")
  const [, setLocation] = useLocation()
  const integrationId = params!.id

  const { integration, loading, refreshIntegration } = useIntegrationSettingsLoader(
    integrationId,
    integrations => findIntegrationById(integrations, integrationId)
  )

  if (loading) {
    return (
      <SettingsLayout>
        <div class="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      </SettingsLayout>
    )
  }

  if (!integration) {
    return (
      <SettingsLayout>
        <div class="text-center py-12">
          <p class="text-gray-500 mb-4">Integration not found</p>
          <Button variant="outline" onClick={() => setLocation("/settings/connections")}>
            Back to Connections
          </Button>
        </div>
      </SettingsLayout>
    )
  }

  return <IntegrationSettingsContent integration={integration} onRefresh={refreshIntegration} />
}
